import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Clock,
    CheckCircle,
    XCircle,
    ExternalLink,
    RefreshCw,
    Beaker,
    FolderOpen,
    ArrowUpDown,
    Loader2,
    Trash2,
    StopCircle,
    Settings2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tooltip } from '../components/ui/tooltip';
import { analysisAPI, coiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Dialog } from '@headlessui/react';

/**
 * AnalysisList page — route: /dashboard
 *
 * The main dashboard for authenticated users. Fetches and displays all
 * analysis runs belonging to the current user in a sortable table.
 *
 * Key behaviours:
 *   - Auto-refreshes the list every 30 seconds while any run is pending or
 *     running, so users see status updates without manual intervention.
 *   - Supports client-side sorting by ID, project name, status, or date.
 *   - Provides per-row actions depending on status:
 *       completed  → Results, Manage, Logs, Delete
 *       running    → Manage, Logs, Stop, Delete
 *       pending    → Manage, Logs, Stop, Delete
 *       failed     → Manage, Logs, Delete
 *   - Delete requires confirmation via a modal to prevent accidents.
 *
 * State:
 *   list            — array of analysis objects from the API
 *   loading         — true during the initial (non-silent) fetch
 *   error           — error message shown in a card when a fetch or action fails
 *   sortField       — the object key currently used for sorting
 *   sortDirection   — 'asc' or 'desc'
 *   refreshing      — true while any fetch (including silent ones) is running;
 *                     drives the spinning icon on the Refresh button
 *   deleteConfirm   — holds the analysisId to delete; non-null means the modal is open
 *   deleting        — holds the analysisId being deleted so the modal button shows a spinner
 *   stopping        — holds the analysisId being stopped so the row button shows a spinner
 *   actionSuccess   — brief success message shown after a successful delete
 */
export default function AnalysisList() {
    const navigate = useNavigate();
    const { user } = useAuth();

    // The full list of analysis objects returned by the API.
    const [list, setList] = useState([]);

    // True during the initial blocking fetch; shows a centered spinner.
    const [loading, setLoading] = useState(true);

    // Error message shown when a fetch or action fails.
    const [error, setError] = useState('');

    // Column key used for client-side sorting.
    const [sortField, setSortField] = useState('created_at');

    // Sort order for the active column — 'asc' or 'desc'.
    const [sortDirection, setSortDirection] = useState('desc');

    // True while any fetch is in progress; spins the Refresh button icon.
    const [refreshing, setRefreshing] = useState(false);

    // When non-null, the delete confirmation modal is open for this analysisId.
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // The analysisId currently being deleted; drives the spinner in the modal.
    const [deleting, setDeleting] = useState(null);

    // The analysisId currently being stopped; drives the spinner in its row button.
    const [stopping, setStopping] = useState(null);

    // The analysisId currently being re-run (COI only); drives the spinner in its row button.
    const [rerunning, setRerunning] = useState(null);

    // Brief success message shown after a successful delete (auto-clears after 3s).
    const [actionSuccess, setActionSuccess] = useState('');

    // Controls the pipeline-chooser dropdown shown by the New Analysis button.
    const [showPipelineMenu, setShowPipelineMenu] = useState(false);

    // Ticks every minute so elapsed-time labels ("Running for Xm") re-render
    // even when the underlying analysis list hasn't changed.
    const [, setNowTick] = useState(0);

    /**
     * handleStop — sends a stop request for a running analysis.
     *
     * Sets `stopping` to the target ID so the row button shows a spinner.
     * Refreshes the list after the call completes regardless of outcome.
     *
     * @param {number} analysisId
     */
    const handleStop = async (analysisId) => {
        try {
            setStopping(analysisId);
            await analysisAPI.stopAnalysis(analysisId);
            fetchList();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to stop analysis');
        } finally {
            setStopping(null);
        }
    };

    /**
     * handleRerunCoi — re-launches a failed COI / Eukaryota analysis.
     *
     * The COI pipeline is single-container and has no per-step Manage page,
     * so the only way to retry is to call POST /coi/{project_id}/start again.
     * That endpoint creates a fresh analysis row (with a new id) and a new
     * m16s-coi-<id> container, reusing the already-uploaded inputs.
     *
     * @param {number} analysisId — the failed analysis id (only used for the row spinner)
     * @param {number} projectId  — the project to re-launch
     */
    const handleRerunCoi = async (analysisId, projectId) => {
        try {
            setRerunning(analysisId);
            setError('');
            await coiAPI.startAnalysis(projectId);
            setActionSuccess('COI analysis re-launched');
            setTimeout(() => setActionSuccess(''), 3000);
            fetchList();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to re-run COI analysis');
            setTimeout(() => setError(''), 5000);
        } finally {
            setRerunning(null);
        }
    };

    /**
     * handleDelete — permanently deletes an analysis after modal confirmation.
     *
     * Closes the modal, shows a success message for 3 seconds, and refreshes
     * the list. Errors are shown for 5 seconds then cleared automatically.
     *
     * @param {number} analysisId
     */
    const handleDelete = async (analysisId) => {
        try {
            setDeleting(analysisId);
            setError('');
            await analysisAPI.deleteAnalysis(analysisId);
            setDeleteConfirm(null);
            setActionSuccess('Analysis deleted successfully');
            setTimeout(() => setActionSuccess(''), 3000);
            fetchList();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete analysis');
            setTimeout(() => setError(''), 5000);
        } finally {
            setDeleting(null);
        }
    };

    /**
     * handleSort — updates the sort field and toggles direction.
     *
     * Clicking the same column header a second time reverses the sort order.
     * Clicking a new column resets direction to ascending.
     *
     * @param {string} field — the analysis object key to sort by
     */
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Derives a sorted copy of `list` without mutating state.
    // created_at is parsed as a Date so numeric comparison works correctly.
    const sortedList = [...list].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        if (sortField === 'created_at') {
            aVal = new Date(aVal);
            bVal = new Date(bVal);
        }
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    /**
     * useEffect — fetches the list on mount and sets up auto-refresh.
     *
     * The interval checks whether any run is pending or running before
     * calling fetchList to avoid unnecessary API calls when everything is
     * already complete. The interval is torn down on unmount.
     *
     * Dependency: `list.length` — re-registers the interval whenever the
     * number of items changes so the staleness check always reflects the
     * current list.
     */
    useEffect(() => {
        fetchList();
    }, []);

    /**
     * useEffect — auto-refresh while any run is pending or running.
     *
     * Depends on `list` so the staleness check always sees the current state
     * (a `[]`-deps interval would capture an empty list forever and never fire).
     * Also ticks `nowTick` every minute to force re-render so the elapsed-time
     * label ("Running for Xm") advances between fetches.
     */
    useEffect(() => {
        const hasActive = list.some(
            a => a.analysis_status === 'pending' || a.analysis_status === 'running'
        );
        if (!hasActive) return undefined;

        const tick = setInterval(() => setNowTick(t => t + 1), 60000);
        const refresh = setInterval(() => fetchList(true), 30000);

        return () => {
            clearInterval(tick);
            clearInterval(refresh);
        };
    }, [list]);

    /**
     * fetchList — loads all analyses from the API.
     *
     * When `silent` is true the full-page loading spinner is skipped; only
     * the smaller Refresh button spinner (`refreshing`) is shown. This keeps
     * auto-refresh unobtrusive.
     *
     * @param {boolean} [silent=false]
     */
    const fetchList = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            setRefreshing(true);
            const response = await analysisAPI.getAllAnalysis();
            setList(response.data.analysis_list);
            setError('');
        } catch (err) {
            setError('Failed to load analyses');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    /**
     * getStatusIcon — returns the icon element that corresponds to a status string.
     *
     * @param {string} status — 'completed' | 'failed' | 'running' | 'pending'
     * @returns {JSX.Element}
     */
    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'failed':
                return <XCircle className="h-5 w-5 text-red-500" />;
            case 'running':
                return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
            case 'pending':
            default:
                return <Clock className="h-5 w-5 text-yellow-500" />;
        }
    };

    /**
     * getStatusBadge — renders a coloured pill badge for a status string.
     *
     * @param {string} status
     * @returns {JSX.Element}
     */
    const getStatusBadge = (status) => {
        const styles = {
            completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
            running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
            pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        };

        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
                {getStatusIcon(status)}
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    /**
     * getTypeBadge — renders a coloured pill badge for the analysis type.
     *
     * 'hybrid' = multi-batch analysis; anything else is treated as a single
     * sample run and rendered in a neutral grey style.
     *
     * @param {string} type
     * @returns {JSX.Element}
     */

    /**
     * formatDate — formats an ISO date string for display in the table.
     *
     * @param {string} dateString
     * @returns {string} e.g. "Jan 15, 2025, 09:30 AM"
     */
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    /**
     * getTimeDisplay — returns a human-readable elapsed or relative time string.
     *
     * Running/pending: "Running for 2h 15m" (elapsed since created_at)
     * Completed:       "Completed 3h ago"   (relative to completed_at)
     * Failed:          "Failed 1d ago"       (relative to completed_at)
     *
     * Falls back to the formatted created_at date if no suitable timestamp exists.
     *
     * @param {Object} analysis
     * @returns {string}
     */
    const getTimeDisplay = (analysis) => {
        const now = new Date();

        const relativeLabel = (ms, prefix) => {
            const mins = Math.floor(ms / 60000);
            const hours = Math.floor(mins / 60);
            const days = Math.floor(hours / 24);
            if (days > 0) return `${prefix} ${days}d ago`;
            if (hours > 0) return `${prefix} ${hours}h ago`;
            return `${prefix} ${mins}m ago`;
        };

        if (analysis.analysis_status === 'running' || analysis.analysis_status === 'pending') {
            const ms = now - new Date(analysis.created_at);
            const mins = Math.floor(ms / 60000);
            const hours = Math.floor(mins / 60);
            const remMins = mins % 60;
            if (hours > 0) return `Running for ${hours}h ${remMins}m`;
            return `Running for ${mins}m`;
        }

        if (analysis.analysis_status === 'completed' && analysis.completed_at) {
            return relativeLabel(now - new Date(analysis.completed_at), 'Completed');
        }

        if (analysis.analysis_status === 'failed' && analysis.completed_at) {
            return relativeLabel(now - new Date(analysis.completed_at), 'Failed');
        }

        return formatDate(analysis.created_at);
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b bg-card">
                <div className="container mx-auto px-4 py-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold">
                                Welcome back, {user?.first_name}.
                            </h1>
                            <p className="text-muted-foreground mt-1">
                                View and manage your analysis runs
                            </p>
                            {!loading && list.length > 0 && (
                                <div className="flex gap-3 mt-3">
                                    {list.filter(a => a.analysis_status === 'completed').length > 0 && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                            <CheckCircle className="h-3.5 w-3.5" />
                                            {list.filter(a => a.analysis_status === 'completed').length} completed
                                        </span>
                                    )}
                                    {list.filter(a => a.analysis_status === 'running' || a.analysis_status === 'pending').length > 0 && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            {list.filter(a => a.analysis_status === 'running' || a.analysis_status === 'pending').length} running
                                        </span>
                                    )}
                                    {list.filter(a => a.analysis_status === 'failed').length > 0 && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                            <XCircle className="h-3.5 w-3.5" />
                                            {list.filter(a => a.analysis_status === 'failed').length} failed
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <Tooltip content="Reload the list to check for status updates">
                                <Button
                                    variant="outline"
                                    onClick={() => fetchList()}
                                    disabled={refreshing}
                                >
                                    <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                                    Refresh
                                </Button>
                            </Tooltip>
                            <div className="relative">
                                <Tooltip content="Pick a pipeline and start a new analysis">
                                    <Button onClick={() => setShowPipelineMenu((v) => !v)}>
                                        <FolderOpen className="h-4 w-4 mr-2" />
                                        New Analysis
                                    </Button>
                                </Tooltip>
                                {showPipelineMenu && (
                                    <>
                                        {/* invisible backdrop closes the menu when clicked */}
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setShowPipelineMenu(false)}
                                        />
                                        <div className="absolute right-0 mt-2 w-72 bg-card border border-border rounded-md shadow-lg z-20 overflow-hidden">
                                            <button
                                                className="w-full text-left px-4 py-3 hover:bg-accent transition-colors"
                                                onClick={() => {
                                                    setShowPipelineMenu(false);
                                                    navigate('/project/batch/new');
                                                }}
                                            >
                                                <div className="font-medium text-sm">16S rRNA (EPI2ME)</div>
                                                <div className="text-xs text-muted-foreground">Bacterial community profiling, Nextflow + R</div>
                                            </button>
                                            <div className="border-t border-border" />
                                            <button
                                                className="w-full text-left px-4 py-3 hover:bg-accent transition-colors"
                                                onClick={() => {
                                                    setShowPipelineMenu(false);
                                                    navigate('/project/coi/new');
                                                }}
                                            >
                                                <div className="font-medium text-sm">COI / Eukaryota (QIIME 2)</div>
                                                <div className="text-xs text-muted-foreground">Eukaryote eDNA profiling using COI primers</div>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="container mx-auto px-4 py-8">
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="ml-3 text-muted-foreground">Loading...</span>
                    </div>
                )}

                {error && (
                    <Card className="border-destructive">
                        <CardContent className="py-6">
                            <div className="flex items-center gap-3 text-destructive">
                                <XCircle className="h-5 w-5" />
                                <span>{error}</span>
                            </div>
                        </CardContent>
                    </Card>
                )}
                {actionSuccess && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-4"
                    >
                        <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                            <CardContent className="py-3">
                                <div className="flex items-center gap-3 text-green-800 dark:text-green-200">
                                    <CheckCircle className="h-5 w-5" />
                                    <span>{actionSuccess}</span>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Empty state shown when the fetch succeeds but returns no items. */}
                {!loading && !error && list.length === 0 && (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Beaker className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                            <h3 className="text-lg font-medium mb-2">No analyses yet</h3>
                            <p className="text-muted-foreground mb-4">
                                Start your first analysis to see results here
                            </p>
                            <div className="flex flex-col gap-3 items-center">
                                <Button onClick={() => navigate('/project/batch/new')}>
                                    16S rRNA (EPI2ME)
                                </Button>
                                <Button variant="outline" onClick={() => navigate('/project/coi/new')}>
                                    COI / Eukaryota (QIIME 2)
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}


                {!loading && !error && list.length > 0 && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg">
                                {list.length} {list.length === 1 ? 'Analysis' : 'Analyses'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b bg-muted/50">
                                            <th
                                                className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                                                onClick={() => handleSort('analysis_id')}
                                            >
                                                <div className="flex items-center gap-2">
                                                    ID
                                                    <ArrowUpDown className="h-4 w-4" />
                                                </div>
                                            </th>
                                            <th
                                                className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                                                onClick={() => handleSort('project_name')}
                                            >
                                                <div className="flex items-center gap-2">
                                                    Project Name
                                                    <ArrowUpDown className="h-4 w-4" />
                                                </div>
                                            </th>
                                            <th
                                                className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                                                onClick={() => handleSort('analysis_status')}
                                            >
                                                <div className="flex items-center gap-2">
                                                    Status
                                                    <ArrowUpDown className="h-4 w-4" />
                                                </div>
                                            </th>
                                            <th
                                                className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                                                onClick={() => handleSort('created_at')}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4" />
                                                    Time
                                                    <ArrowUpDown className="h-4 w-4" />
                                                </div>
                                            </th>
                                            <th className="text-left p-4 font-medium text-muted-foreground">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedList.map((analysis, index) => (
                                            // Each row animates in from below with a staggered delay.
                                            <motion.tr
                                                key={analysis.analysis_id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                                            >
                                                <td className="p-4">
                                                    <span className="font-mono text-sm">
                                                        #{analysis.analysis_id}
                                                    </span>
                                                </td>
                                                <td className="p-4">
                                                    <span className="text-sm">
                                                        {analysis.project_name || '—'}
                                                    </span>
                                                </td>
                                                <td className="p-4">
                                                    {getStatusBadge(analysis.analysis_status)}
                                                </td>
                                                <td className="p-4 text-sm text-muted-foreground">
                                                    {getTimeDisplay(analysis)}
                                                </td>
                                                <td className="p-4">
                                                    {/* Actions differ by status. Completed runs expose
                                                        the Results button; in-progress runs expose Stop. */}
                                                    {analysis.analysis_status === 'completed' && (
                                                        <div className="flex gap-2">
                                                            <Tooltip content="View interactive plots and analysis results">
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => navigate(
                                                                        analysis.analysis_type === 'coi_eukaryota'
                                                                            ? `/analysis/${analysis.analysis_id}/coi-results`
                                                                            : `/analysis/${analysis.analysis_id}/results`
                                                                    )}
                                                                >
                                                                    <ExternalLink className="h-4 w-4 mr-1.5" />
                                                                    Results
                                                                </Button>
                                                            </Tooltip>
                                                            {analysis.analysis_type !== 'coi_eukaryota' && (
                                                                <Tooltip content="Monitor batch progress and re-run pipeline steps">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => navigate(`/analysis/${analysis.analysis_id}/manage`)}
                                                                    >
                                                                        <Settings2 className="h-4 w-4 mr-1.5" />
                                                                        Manage
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip content="Permanently delete this analysis and all its data">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => setDeleteConfirm(analysis.analysis_id)}
                                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                >
                                                                    <Trash2 className="h-4 w-4 mr-1.5" />
                                                                    Delete
                                                                </Button>
                                                            </Tooltip>
                                                        </div>
                                                    )}
                                                    {(analysis.analysis_status === 'pending' || analysis.analysis_status === 'running') && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-muted-foreground flex items-center gap-2">
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                                Processing...
                                                            </span>
                                                            {analysis.analysis_type !== 'coi_eukaryota' && (
                                                                <Tooltip content="Monitor batch progress and re-run pipeline steps">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => navigate(`/analysis/${analysis.analysis_id}/manage`)}
                                                                    >
                                                                        <Settings2 className="h-4 w-4 mr-1.5" />
                                                                        Manage
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip content="Halt this analysis — it can be restarted from the Manage page">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleStop(analysis.analysis_id)}
                                                                    disabled={stopping === analysis.analysis_id}
                                                                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                                                >
                                                                    {stopping === analysis.analysis_id ? (
                                                                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                                                    ) : (
                                                                        <StopCircle className="h-4 w-4 mr-1.5" />
                                                                    )}
                                                                    Stop
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip content="Permanently delete this analysis and all its data">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => setDeleteConfirm(analysis.analysis_id)}
                                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                >
                                                                    <Trash2 className="h-4 w-4 mr-1.5" />
                                                                    Delete
                                                                </Button>
                                                            </Tooltip>
                                                        </div>
                                                    )}
                                                    {analysis.analysis_status === 'failed' && (
                                                        <div className="flex gap-2">
                                                            {analysis.analysis_type !== 'coi_eukaryota' && (
                                                                <Tooltip content="Re-run sequencing or visualization from the Manage page">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => navigate(`/analysis/${analysis.analysis_id}/manage`)}
                                                                    >
                                                                        <Settings2 className="h-4 w-4 mr-1.5" />
                                                                        Manage
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                            {analysis.analysis_type === 'coi_eukaryota' && analysis.project_id && (
                                                                <Tooltip content="Re-launch the COI pipeline for this project (reuses the uploaded files)">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => handleRerunCoi(analysis.analysis_id, analysis.project_id)}
                                                                        disabled={rerunning === analysis.analysis_id}
                                                                    >
                                                                        {rerunning === analysis.analysis_id ? (
                                                                            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                                                        ) : (
                                                                            <RefreshCw className="h-4 w-4 mr-1.5" />
                                                                        )}
                                                                        Re-run
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip content="Permanently delete this analysis and all its data">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => setDeleteConfirm(analysis.analysis_id)}
                                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                >
                                                                    <Trash2 className="h-4 w-4 mr-1.5" />
                                                                    Delete
                                                                </Button>
                                                            </Tooltip>
                                                        </div>
                                                    )}
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Auto-refresh notice — only shown when at least one run is active. */}
                {list.some(a => a.analysis_status === 'pending' || a.analysis_status === 'running') && (
                    <p className="text-center text-sm text-muted-foreground mt-4">
                        Auto-refreshing every 30 seconds
                    </p>
                )}
            </main>

            {/* Delete Confirmation Modal
                `deleteConfirm` holds the analysisId; modal is open when it is non-null. */}
            <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)} className="relative z-50">
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-background border rounded-lg p-6 max-w-sm w-full shadow-lg">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-destructive/10 rounded-full">
                                <Trash2 className="h-6 w-6 text-destructive" />
                            </div>
                            <Dialog.Title className="text-lg font-semibold">Delete Analysis?</Dialog.Title>
                        </div>
                        <p className="text-muted-foreground mb-6">
                            This will permanently delete analysis #{deleteConfirm} and all its results. This action cannot be undone.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => handleDelete(deleteConfirm)}
                                disabled={deleting === deleteConfirm}
                            >
                                {deleting === deleteConfirm ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </Button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>

        </div>
    );
}
